import { defineStore } from 'pinia';
import type { ITask, ITaskInput, ITasksCounts } from '@torim-app/types';
import { TasksApi } from '@app/api/admin/tasks-api';

interface IAdminTasksStore {
  counts: ITasksCounts;
  /**
   * Bumped after every change. Each task surface (tasks page, calendar strip, customer tab,
   * appointment checklist) fetches its own slice and re-fetches when this moves, so a task
   * ticked in one place updates everywhere without a shared cache to keep consistent.
   */
  version: number;
  /** Bumped when a change may have moved a task's reserved block on the calendar. */
  calendarVersion: number;
}

export const useAdminTasksStore = defineStore('admin-tasks', {
  state: (): IAdminTasksStore => ({
    counts: { dueNow: 0, overdue: 0 },
    version: 0,
    calendarVersion: 0,
  }),

  getters: {
    getDueNowCount: (state): number => state.counts.dueNow,
  },

  actions: {
    async fetchCounts(): Promise<void> {
      try {
        this.counts = await TasksApi.counts();
      } catch (e) {
        console.error(e);
      }
    },

    changed(touchesCalendar = false): void {
      this.version++;
      if (touchesCalendar) this.calendarVersion++;
      this.fetchCounts();
    },

    async create(input: ITaskInput): Promise<ITask> {
      const task = await TasksApi.create(input);
      this.changed(!!task.reservation);
      return task;
    },

    /** `hadReservation`: the task held calendar time before this edit. */
    async update(id: string, input: ITaskInput, hadReservation = false): Promise<ITask> {
      const task = await TasksApi.update(id, input);
      this.changed(hadReservation || !!task.reservation);
      return task;
    },

    async setDone(id: string, done: boolean): Promise<ITask> {
      const task = await TasksApi.setDone(id, done);
      // A recurring reserved task books its next block on completion.
      this.changed(!!task.reservation);
      return task;
    },

    async remove(id: string, hadReservation = true): Promise<void> {
      await TasksApi.remove(id);
      this.changed(hadReservation);
    },
  },
});
